import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Sunrise, Globe2, Building2, AlertCircle } from "lucide-react";
import { Card, Badge, SectionLabel } from "../components/ui-bits";
import { liveAPI } from "../data/liveApi";
import { SeoHead } from "../seo/SeoHead";

function fmtGeneratedLabel(iso) {
  // "03 Jul 2026 • 07:00 AM IST"
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "2-digit", month: "short", year: "numeric",
    });
    const time = d.toLocaleTimeString("en-US", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
    return `${date} • ${time} IST`;
  } catch {
    return iso;
  }
}

function msUntilNext7amIST() {
  // Compute how many ms until the next 07:00 IST relative to the browser's clock.
  const nowUtcMs = Date.now();
  const ist = new Date(nowUtcMs + 5.5 * 60 * 60 * 1000);
  const target = new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate(), 7, 0, 0));
  if (target.getTime() <= ist.getTime()) target.setUTCDate(target.getUTCDate() + 1);
  const istTargetMs = target.getTime();
  // Convert back to real (browser) local ms
  return istTargetMs - ist.getTime();
}

const Row = ({ label, value, positive }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-fij-border last:border-b-0">
    <span className="font-mono text-[11px] text-fij-textDim">{label}</span>
    <span className={`font-mono text-xs ${positive === true ? "text-fij-green" : positive === false ? "text-fij-red" : "text-fij-text"}`}>
      {value}
    </span>
  </div>
);

function findIndex(list, ticker) {
  return (list || []).find((x) => (x.ticker || x.symbol || "").toUpperCase() === ticker.toUpperCase());
}

function formatQuote(q) {
  if (!q || q.value === "—" || !q.value) return { text: "—", positive: null };
  const change = q.change_pct || q.change || "";
  return {
    text: `${q.value}  ${change}`.trim(),
    positive: q.positive,
  };
}

export default function MorningBrief() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBrief = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await liveAPI.morningBrief();
      setBrief(data);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
        "Live market data is temporarily unavailable. Retrying automatically…"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch once on mount. The brief is a daily publication — the backend cache
  // pins it until 07:00 IST tomorrow. We schedule ONE refresh at that boundary
  // (and reschedule after each refresh) so users who leave the app open see the
  // new day's brief without any manual interaction.
  useEffect(() => {
    fetchBrief();
  }, [fetchBrief]);

  useEffect(() => {
    const delay = Math.max(msUntilNext7amIST(), 60_000);
    const t = setTimeout(fetchBrief, delay);
    return () => clearTimeout(t);
  }, [fetchBrief, brief?.next_refresh_at]);

  // Derived data (never fall back to mock)
  const briefText = brief?.brief_text || "";
  const overnightUs = brief?.overnight_us_summary || "";
  const globalSummary = brief?.global_markets_summary || "";
  const indiaPre = brief?.india_premarket || {};
  const risks = brief?.risks_today || [];
  const opps = brief?.opportunities_today || [];
  const watchlist = brief?.watchlist || [];
  const md = brief?.market_data || {};
  const newsUsed = brief?.news_used || [];
  const generatedAt = brief?.generated_at;

  // US markets
  const dow = useMemo(() => formatQuote(findIndex(md.us, "DOW")), [md.us]);
  const nasdaq = useMemo(() => formatQuote(findIndex(md.us, "NASDAQ")), [md.us]);
  const sp500 = useMemo(() => formatQuote(findIndex(md.us, "S&P 500")), [md.us]);
  const tnx = useMemo(() => formatQuote(findIndex(md.commodities, "US 10Y")), [md.commodities]);
  const dxy = useMemo(() => formatQuote(findIndex(md.commodities, "DOLLAR INDEX")), [md.commodities]);
  const brent = useMemo(() => formatQuote(findIndex(md.commodities, "BRENT CRUDE")), [md.commodities]);
  const wti = useMemo(() => formatQuote(findIndex(md.commodities, "WTI CRUDE")), [md.commodities]);
  const gold = useMemo(() => formatQuote(findIndex(md.commodities, "GOLD")), [md.commodities]);
  const nikkei = useMemo(() => formatQuote(findIndex(md.global, "NIKKEI 225")), [md.global]);
  const hangseng = useMemo(() => formatQuote(findIndex(md.global, "HANG SENG")), [md.global]);
  const shanghai = useMemo(() => formatQuote(findIndex(md.global, "SHANGHAI COMPOSITE")), [md.global]);
  const ftse = useMemo(() => formatQuote(findIndex(md.global, "FTSE 100")), [md.global]);
  const dax = useMemo(() => formatQuote(findIndex(md.global, "DAX")), [md.global]);
  const gift = useMemo(() => formatQuote(md.gift_nifty), [md.gift_nifty]);

  const showEmpty = !loading && !brief;

  return (
    <div className="space-y-6">
      <SeoHead path="/morning-brief" />
      {/* Hero */}
      <Card className="relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full bg-fij-green/5 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Sunrise size={16} className="text-fij-amber" />
              <SectionLabel>MORNING_BRIEF · IST</SectionLabel>
            </div>
            <h2 className="font-mono text-2xl sm:text-3xl text-fij-text mt-1">Pre-market intelligence · India</h2>
            <p className="text-fij-textDim text-sm mt-1">
              Daily institutional digest — refreshes automatically at 07:00 IST.
            </p>
            <div
              className="mt-2 font-mono text-[11px] text-fij-textMuted"
              data-testid="brief-generated-at"
            >
              Morning Brief • Generated on {fmtGeneratedLabel(generatedAt)}
            </div>
          </div>
        </div>
      </Card>

      {error && (
        <Card className="flex items-start gap-3 border-fij-red/30 bg-fij-red/5" data-testid="brief-error">
          <AlertCircle size={16} className="text-fij-red shrink-0 mt-0.5" />
          <div className="font-mono text-xs text-fij-red">{error}</div>
        </Card>
      )}

      {showEmpty && !error && (
        <Card className="text-center py-12 font-mono text-sm text-fij-textMuted" data-testid="brief-empty">
          Live market data is temporarily unavailable. Retrying automatically…
        </Card>
      )}

      {/* AI summary */}
      {brief && (
        <Card data-testid="brief-card">
          <div className="flex items-center justify-between mb-3">
            <SectionLabel>AI BRIEFING</SectionLabel>
            <Badge tone="positive">AI · Claude Sonnet</Badge>
          </div>
          <p className="text-fij-text text-sm sm:text-base leading-relaxed font-sans" data-testid="brief-text">
            {briefText || "Generating…"}
          </p>
          <div className="mt-5 pt-4 border-t border-fij-border flex items-center justify-between font-mono text-[10px] text-fij-textMuted">
            <span>SOURCE · live market data + {newsUsed.length} recent headlines (≤{brief?.news_window_hours || 48}h)</span>
            <span>DAILY EDITION · locked until next 07:00 IST</span>
          </div>
        </Card>
      )}

      {brief && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Overnight US Summary */}
        <Card data-testid="us-summary">
          <div className="flex items-center justify-between mb-3">
            <SectionLabel>US MARKETS · PREVIOUS SESSION</SectionLabel>
            <Building2 size={14} className="text-fij-textDim" />
          </div>
          <Row label="Dow Jones" value={dow.text} positive={dow.positive} />
          <Row label="Nasdaq" value={nasdaq.text} positive={nasdaq.positive} />
          <Row label="S&P 500" value={sp500.text} positive={sp500.positive} />
          <Row label="US 10Y Yield" value={tnx.text} />
          <Row label="Dollar Index" value={dxy.text} />
          {overnightUs && (
            <p className="mt-3 pt-3 border-t border-fij-border text-xs text-fij-text leading-relaxed">
              {overnightUs}
            </p>
          )}
        </Card>

        {/* Global Markets */}
        <Card data-testid="global-summary">
          <div className="flex items-center justify-between mb-3">
            <SectionLabel>GLOBAL MARKETS</SectionLabel>
            <Globe2 size={14} className="text-fij-textDim" />
          </div>
          <Row label="Nikkei 225" value={nikkei.text} positive={nikkei.positive} />
          <Row label="Hang Seng" value={hangseng.text} positive={hangseng.positive} />
          <Row label="Shanghai Composite" value={shanghai.text} positive={shanghai.positive} />
          <Row label="FTSE 100" value={ftse.text} positive={ftse.positive} />
          <Row label="DAX" value={dax.text} positive={dax.positive} />
          <Row label="Brent Crude" value={brent.text} positive={brent.positive} />
          <Row label="WTI" value={wti.text} positive={wti.positive} />
          <Row label="Gold" value={gold.text} positive={gold.positive} />
          {globalSummary && (
            <p className="mt-3 pt-3 border-t border-fij-border text-xs text-fij-text leading-relaxed">
              {globalSummary}
            </p>
          )}
        </Card>
      </div>
      )}

      {/* India pre-market intel */}
      {brief && (
      <Card data-testid="india-premarket">
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>PRE-MARKET INDIA INTELLIGENCE</SectionLabel>
          <Badge tone="info">
            GIFT NIFTY · {gift.text}
          </Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="space-y-3">
            <div>
              <SectionLabel>Expected Sentiment</SectionLabel>
              <div className="mt-1 text-fij-text text-sm">{indiaPre.expected_sentiment || "—"}</div>
            </div>

            <div>
              <SectionLabel>Overnight Developments</SectionLabel>
              <ul className="mt-1.5 space-y-1.5">
                {(indiaPre.overnight_developments || []).map((o) => (
                  <li key={o} className="flex items-start gap-2 text-xs text-fij-text">
                    <span className="font-mono text-fij-green mt-0.5">›</span>
                    <span>{o}</span>
                  </li>
                ))}
                {!(indiaPre.overnight_developments || []).length && (
                  <li className="text-xs text-fij-textMuted">—</li>
                )}
              </ul>
            </div>

            <div>
              <SectionLabel>Sectors In Focus</SectionLabel>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(indiaPre.sectors_in_focus || []).map((s) => (
                  <Badge key={s} tone="positive">{s}</Badge>
                ))}
                {!(indiaPre.sectors_in_focus || []).length && (
                  <span className="font-mono text-[10px] text-fij-textMuted">—</span>
                )}
              </div>
            </div>

            <div>
              <SectionLabel>Stocks Likely Active</SectionLabel>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(indiaPre.active_stocks || []).map((s) => (
                  <span key={s} className="font-mono text-[10px] text-fij-text border border-fij-border rounded-sm px-1.5 py-0.5">{s}</span>
                ))}
                {!(indiaPre.active_stocks || []).length && (
                  <span className="font-mono text-[10px] text-fij-textMuted">—</span>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <Row label="FII / DII (prev session)" value={indiaPre.fii_dii || "—"} />
            <Row label="Rupee outlook" value={indiaPre.rupee || "—"} />
            <Row label="GIFT Nifty read" value={indiaPre.gift_nifty || "—"} positive />

            <div className="pt-2">
              <SectionLabel>Economic Calendar</SectionLabel>
              <ul className="mt-1.5 space-y-1 text-xs text-fij-text font-mono">
                {(indiaPre.calendar || []).map((c) => (<li key={c}>· {c}</li>))}
                {!(indiaPre.calendar || []).length && (<li className="text-fij-textMuted">·  —</li>)}
              </ul>
            </div>

            <div>
              <SectionLabel>Corporate Announcements</SectionLabel>
              <ul className="mt-1.5 space-y-1 text-xs text-fij-text font-mono">
                {(indiaPre.corporate || []).map((c) => (<li key={c}>· {c}</li>))}
                {!(indiaPre.corporate || []).length && (<li className="text-fij-textMuted">·  —</li>)}
              </ul>
            </div>
          </div>
        </div>
      </Card>
      )}

      {/* Risks + Opportunities */}
      {brief && (risks.length > 0 || opps.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <Card data-testid="risks-today">
            <SectionLabel>RISKS TODAY</SectionLabel>
            <ul className="mt-2 space-y-1.5">
              {risks.map((r) => (
                <li key={r} className="flex items-start gap-2 text-xs text-fij-text">
                  <span className="font-mono text-fij-red mt-0.5">▲</span>
                  <span>{r}</span>
                </li>
              ))}
              {!risks.length && <li className="text-xs text-fij-textMuted">—</li>}
            </ul>
          </Card>
          <Card data-testid="opportunities-today">
            <SectionLabel>OPPORTUNITIES TODAY</SectionLabel>
            <ul className="mt-2 space-y-1.5">
              {opps.map((o) => (
                <li key={o} className="flex items-start gap-2 text-xs text-fij-text">
                  <span className="font-mono text-fij-green mt-0.5">▼</span>
                  <span>{o}</span>
                </li>
              ))}
              {!opps.length && <li className="text-xs text-fij-textMuted">—</li>}
            </ul>
          </Card>
        </div>
      )}

      {/* Watchlist */}
      {brief && (
      <Card data-testid="nifty-watch">
        <div className="flex items-center justify-between mb-3">
          <SectionLabel>TOP 8 NIFTY 50 STOCKS TO WATCH TODAY</SectionLabel>
          <span className="font-mono text-[10px] text-fij-textMuted">
            AI-generated · pre-open · {watchlist.length} picks
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {watchlist.map((w) => (
            <div
              key={w.ticker}
              data-testid={`watch-${w.ticker}`}
              className="p-3 border border-fij-border rounded-sm hover:border-fij-green/30 transition-colors flex flex-col gap-2"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-fij-text">{w.ticker}</span>
                <Badge tone={w.bias === "Bullish" ? "positive" : w.bias === "Bearish" ? "negative" : "neutral"}>
                  {w.bias || "Neutral"}
                </Badge>
              </div>
              <div className="text-[11px] text-fij-textDim leading-snug">{w.name}</div>
              <p className="text-[11px] text-fij-text leading-snug">{w.reason}</p>
              <div className="mt-auto pt-2 border-t border-fij-border">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-[9px] text-fij-textMuted uppercase">Confidence</span>
                  <span className="font-mono text-[10px] text-fij-green">{w.confidence ?? "—"}/100</span>
                </div>
                <div className="h-1 bg-fij-border rounded-sm overflow-hidden">
                  <div className="h-full bg-fij-green" style={{ width: `${w.confidence || 0}%` }} />
                </div>
                <div className="mt-2 font-mono text-[9px] text-fij-textMuted uppercase">Catalyst</div>
                <div className="text-[10px] text-fij-text leading-snug mt-0.5">{w.catalyst || "—"}</div>
              </div>
            </div>
          ))}
          {watchlist.length === 0 && (
            <div className="sm:col-span-2 lg:col-span-4 py-8 text-center font-mono text-xs text-fij-textMuted">
              Watchlist regeneration in progress…
            </div>
          )}
        </div>
      </Card>
      )}
    </div>
  );
}
